import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Stack,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PaletteIcon from "@mui/icons-material/Palette";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SettingsIcon from "@mui/icons-material/Settings";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import Header from "../../components/Header";
import NotificationSettings from "../../components/Notifications/NotificationSettings";
import { useTheme } from "../../store/contexts/ThemeContext";
import { THEME_MODES } from "../../constants/theme";

// ─── Global media-download preference ────────────────────────────────────────
const GLOBAL_PREFS_KEY = "globalPrefs";
const loadGlobalPrefs = () => {
  try { return JSON.parse(localStorage.getItem(GLOBAL_PREFS_KEY)) || {}; }
  catch { return {}; }
};
const saveGlobalPrefs = (prefs) => {
  try { localStorage.setItem(GLOBAL_PREFS_KEY, JSON.stringify(prefs)); } catch {}
};
export { GLOBAL_PREFS_KEY, loadGlobalPrefs }; // consumed by ImageMessageBubble
// ─────────────────────────────────────────────────────────────────────────────

const SettingsPage = () => {
  const { mode, setMode, fontSize, setFontSize, fontSizeOptions } = useTheme();
  const [globalPrefs, setGlobalPrefs] = React.useState(loadGlobalPrefs);

  const updateGlobalPref = (key, value) => {
    const next = { ...globalPrefs, [key]: value };
    setGlobalPrefs(next);
    saveGlobalPrefs(next);
  };

  const themeOptions = [
    { value: THEME_MODES.LIGHT, label: "Light" },
    { value: THEME_MODES.DARK, label: "Dark" },
  ];

  const displayFontSizeOptions = [
    { value: fontSizeOptions.LARGE, label: "Large" },
    { value: fontSizeOptions.MEDIUM, label: "Medium" },
    { value: fontSizeOptions.SMALL, label: "Small" },
    { value: fontSizeOptions.TINY, label: "Tiny" },
  ];

  return (
    <Box
      sx={(theme) => ({
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, #0a0a0a 0%, #101114 100%)"
            : "linear-gradient(180deg, #f8f9fa 0%, #eef2f7 100%)",
        pb: 8,
      })}
    >
      <Header />

      <Box
        sx={{
          width: "min(1100px, 92%)",
          mx: "auto",
          pt: 4,
          display: "grid",
          gap: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 4,
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)}, ${alpha(theme.palette.common.black, 0.25)})`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.common.white, 0.95)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: "1.5rem", md: "2rem" } }}>
              App Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Personalize the display, theme, and notification behavior of the app.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={(theme) => ({
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            backgroundColor: alpha(theme.palette.background.paper, 0.92),
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <PaletteIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Theme
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            {themeOptions.map((option) => (
              <Button
                key={option.value}
                variant={mode === option.value ? "contained" : "outlined"}
                onClick={() => setMode(option.value)}
                sx={{ textTransform: "none" }}
              >
                {option.label}
              </Button>
            ))}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={(theme) => ({
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            backgroundColor: alpha(theme.palette.background.paper, 0.92),
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <TextFieldsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Font Size
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
            {displayFontSizeOptions.map((option) => (
              <Button
                key={option.value}
                variant={fontSize === option.value ? "contained" : "outlined"}
                onClick={() => setFontSize(option.value)}
                sx={{ textTransform: "none" }}
              >
                {option.label}
              </Button>
            ))}
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="body2" color="text.secondary">
            Changes are saved automatically and applied across the app.
          </Typography>
        </Paper>

        <NotificationSettings />

        {/* ── Storage & Data ─────────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 4,
            border: `1px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.06) : alpha("#000", 0.06)}`,
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <DataUsageIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6" fontWeight={700}>Storage &amp; Data</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Control how media is handled when you receive messages.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={globalPrefs.autoDownloadMedia !== false}
                onChange={(e) => updateGlobalPref("autoDownloadMedia", e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>Auto-download images</Typography>
                <Typography variant="caption" color="text.secondary">
                  {globalPrefs.autoDownloadMedia !== false
                    ? "Images load automatically when received"
                    : "Tap an image to download it — saves data on mobile"}
                </Typography>
              </Box>
            }
            sx={{ ml: 0, alignItems: "flex-start", "& .MuiSwitch-root": { mt: 0.25 } }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default SettingsPage;
